// Krug 5 (K12.1): the item's tier (T1–T4) where gear is shown: the item detail, inventory cards,
// the slot picker, the Trader's gear, Workshop recipes and loot tiles. A hover or a tap explains it;
// red when the survivor's level is below the tier (the rule itself is F8.4 and does not change).
// 23. 10. 2026 (BACKLOG #55): framed when it cannot be worn yet (two tiers up), softly framed when it is worn as the top of
// the survivor's tier until its level (one tier up, `data-grows`).
// Consumables, resources and special stacks have no tier and never get the tag.

import { TIER_LEVELS, TIER_UP_RULES, TIER_WEAR, findItemTemplate, wearLevel, wearableTier } from '@lastday/shared';
import { useMe } from '@/api/hooks';
import { t } from '@/i18n';
import { cx } from '@/lib/format';
import { InlineTip } from './InlineTip';
import { LearnMore } from './Tip';

type Tier = 1 | 2 | 3 | 4;

/** "Tier sets the item's base strength and the level it has its full stats from. T1 Lv 1 · T2 Lv 6 · … One tier above…" */
export function tierExplain(): string {
  const levels = (Object.keys(TIER_LEVELS) as unknown as Tier[]).map((n) => t('common.tierLevel', { n, lv: TIER_LEVELS[n][0] })).join(' · ');
  const text = t('common.tierExplain', { levels });
  return TIER_WEAR.on && TIER_WEAR.ahead > 0 ? `${text} ${t('common.tierAhead')}` : text;
}

/** The tier of an item template, or null for anything without one. */
export function tierOf(templateId: string | null | undefined): Tier | null {
  return (findItemTemplate(templateId)?.tier as Tier | undefined) ?? null;
}

/**
 * 1. 10. 2026 (C4): the tier a Relic Forge recipe makes: the survivor's tier when the item's own is lower
 * (the warning "outclassed" of K16.3 is gone with it), else the item's.
 */
export function forgeTier(templateId: string | null | undefined, level: number | null | undefined): Tier | null {
  const tier = tierOf(templateId);
  if (tier == null) return null;
  return TIER_UP_RULES.on && level != null ? (Math.max(tier, wearableTier(level)) as Tier) : tier;
}

/**
 * `T2`, with the explanation on hover or tap. `level` is the survivor's (default: from /me); below the
 * tier's first level the tag is red. Inside a card (a button) the tag stops the click from opening it.
 */
export function TierTag({ templateId, tier: given, level, className }: { templateId?: string | null; tier?: Tier | null; level?: number; className?: string }) {
  const me = useMe().data?.survivor?.level;
  const tier = given ?? tierOf(templateId);
  const lv = level ?? me;
  // 23. 10. 2026 (BACKLOG #55): too high = cannot be put on yet; grows = worn capped until the tier's level.
  const tooHigh = tier != null && lv != null && lv < wearLevel(templateId ?? '', tier);
  const grows = tier != null && lv != null && !tooHigh && lv < TIER_LEVELS[tier][0];
  if (tier == null) return null;
  return (
    <InlineTip
      ariaLabel={t('common.tierAria', { n: tier })}
      tagProps={{ 'data-tier': tier, 'data-too-high': tooHigh ? 'true' : undefined, 'data-grows': grows ? 'true' : undefined }}
      tipProps={{ 'data-tier-tip': true }}
      className={cx(tooHigh ? 'rounded-[2px] font-semibold text-text ring-1 ring-text' : grows ? 'rounded-[2px] text-text ring-1 ring-muted' : 'text-inherit', className)}
      tip={
        <>
          <span>{tierExplain()}</span>
          <LearnMore section="tiers" className="self-start" />
        </>
      }
    >
      {t('common.tier', { n: tier })}
    </InlineTip>
  );
}

/** 30. 9. 2026: "PWR compares gear of the same tier. It's measured on a level 5 survivor for T1, …" */
export function pwrExplain(): string {
  const at = (n: Tier) => TIER_LEVELS[n][1];
  return t('inventory.pwrTip', { t1: at(1), t2: at(2), t3: at(3), t4: at(4) });
}

/**
 * `PWR 618` with the explanation on hover or tap (30. 9. 2026): PWR is measured on a reference survivor
 * of the item's own tier, so it only compares gear of the same tier. `short`: the number alone (a card
 * with a 4-digit PWR), the whole label in the tip's aria text.
 */
export function PwrTag({ value, short, className }: { value: number; short?: boolean; className?: string }) {
  return (
    <InlineTip ariaLabel={`${t('inventory.pwr', { n: value })}. ${pwrExplain()}`} tagProps={{ 'data-pwr': value }} tipProps={{ 'data-pwr-tip': true }} className={cx('text-inherit', className)} tip={<span>{pwrExplain()}</span>}>
      {short ? String(value) : t('inventory.pwr', { n: value })}
    </InlineTip>
  );
}
